import { Node, mergeAttributes, JSONContent, CommandProps } from '@tiptap/core';
import './LayoutExtension.css';

export interface LayoutOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    layout: {
      insertColumnLayout: (columns: number) => ReturnType;
      insertGridLayout: (rows: number, columns: number) => ReturnType;
      wrapSelectionInColumns: (columns: number) => ReturnType;
      wrapSelectionInGrid: (rows: number, columns: number) => ReturnType;
      swapColumns: (fromIndex: number, toIndex: number) => ReturnType;
    };
  }
}

const createColumnParagraph = (index: number, content: JSONContent | JSONContent[] = { type: 'text', text: `Column ${index + 1} content` }): JSONContent => ({
  type: 'paragraph',
  attrs: { 'data-column': (index + 1).toString(), 'data-draggable': 'true' },
  content: Array.isArray(content) ? content : [content],
});

const createGridParagraph = (index: number, columns: number, content: JSONContent | JSONContent[] = { type: 'text', text: `Grid ${index + 1} content` }): JSONContent => ({
  type: 'paragraph',
  attrs: {
    'data-column': ((index % columns) + 1).toString(),
    'data-row': ((Math.floor(index / columns)) + 1).toString(),
    'data-draggable': 'true',
  },
  content: Array.isArray(content) ? content : [content],
});

const processContentNodes = (nodes: JSONContent[]): JSONContent[] => {
  const processed: JSONContent[] = [];
  for (const node of nodes) {
    if (node.type === 'customImage') {
      processed.push(node);
    } else if (node.type === 'paragraph' && node.content) {
      processed.push(...node.content);
    } else if (node.type === 'text') {
      processed.push(node);
    } else if (node.content && Array.isArray(node.content)) {
      processed.push(...node.content);
    } else {
      processed.push({ type: 'text', text: ' ' });
    }
  }
  return processed;
};

const distributeContent = (contentNodes: JSONContent[], columns: number, isGrid = false, rows = 1): JSONContent[] => {
  const totalCells = isGrid ? rows * columns : columns;
  const actualCells = Math.min(totalCells, contentNodes.length);
  const nodesPerCell = Math.ceil(contentNodes.length / actualCells);
  
  const cellContents: JSONContent[] = [];
  for (let i = 0; i < actualCells; i++) {
    const startIndex = i * nodesPerCell;
    const endIndex = Math.min((i + 1) * nodesPerCell, contentNodes.length);
    const cellNodes = contentNodes.slice(startIndex, endIndex);
    const processedContent = processContentNodes(cellNodes);
    
    if (isGrid) {
      cellContents.push(createGridParagraph(i, columns, processedContent.length ? processedContent : [{ type: 'text', text: ' ' }]));
    } else {
      cellContents.push(createColumnParagraph(i, processedContent.length ? processedContent : [{ type: 'text', text: ' ' }]));
    }
  }
  
  return cellContents;
};

const findLayoutNode = (state: CommandProps['state']) => {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth >= 0; depth--) {
    if ($from.node(depth).type.name === 'layout') {
      return { pos: $from.before(depth), node: $from.node(depth) };
    }
  }
  return null;
};

export const LayoutExtension = Node.create<LayoutOptions>({
  name: 'layout',
  group: 'block',
  content: 'block+',
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      type: {
        default: 'column',
        parseHTML: element => element.getAttribute('data-layout-type'),
        renderHTML: attributes => {
          return {
            'data-layout-type': attributes.type,
          };
        },
      },
      columns: {
        default: 2,
        parseHTML: element => parseInt(element.getAttribute('data-columns') || '2'),
        renderHTML: attributes => {
          return {
            'data-columns': attributes.columns,
          };
        },
      },
      rows: {
        default: 1,
        parseHTML: element => parseInt(element.getAttribute('data-rows') || '1'),
        renderHTML: attributes => {
          return {
            'data-rows': attributes.rows,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-layout-type]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const type = HTMLAttributes['data-layout-type'];
    const columns = parseInt(HTMLAttributes['data-columns']) || 2;
    const rows = parseInt(HTMLAttributes['data-rows']) || 1;
    
    const style = type === 'column' 
      ? `display: grid; grid-template-columns: repeat(${columns}, 1fr); gap: 1rem;`
      : `display: grid; grid-template-columns: repeat(${columns}, 1fr); grid-template-rows: repeat(${rows}, auto); gap: 1rem;`;

    return ['div', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { 
      style,
      'data-layout-type': type,
      'data-columns': columns,
      'data-rows': rows
    }), 0];
  },

  addCommands() {
    return {
      insertColumnLayout:
        (columns: number) =>
        ({ commands }) => {
          const content = Array(columns).fill(null).map((_, index) => createColumnParagraph(index));
          return commands.insertContent({
            type: this.name,
            attrs: { type: 'column', columns, rows: 1 },
            content,
          });
        },
      insertGridLayout:
        (rows: number, columns: number) =>
        ({ commands }) => {
          const content = Array(rows * columns).fill(null).map((_, index) => createGridParagraph(index, columns));
          return commands.insertContent({
            type: this.name,
            attrs: { type: 'grid', columns, rows },
            content,
          });
        },
      wrapSelectionInColumns:
        (columns: number) =>
        ({ commands, state }) => {
          const { from, to } = state.selection;
          const selectedContent = state.doc.slice(from, to);
          
          if (selectedContent.content.size === 0) return false;

          const contentJSON = selectedContent.content.toJSON();
          const contentNodes = contentJSON?.content || (Array.isArray(contentJSON) ? contentJSON : contentJSON?.type ? [contentJSON] : []);
          
          if (contentNodes.length === 0) return false;

          const actualColumns = Math.min(columns, contentNodes.length);
          const columnContents = distributeContent(contentNodes, actualColumns);

          return commands.insertContentAt(
            { from, to },
            {
              type: this.name,
              attrs: { type: 'column', columns: actualColumns, rows: 1 },
              content: columnContents,
            }
          );
        },
      wrapSelectionInGrid:
        (rows: number, columns: number) =>
        ({ commands, state }) => {
          const { from, to } = state.selection;
          const selectedContent = state.doc.slice(from, to);
          
          if (selectedContent.content.size === 0) return false;

          const contentNodes = selectedContent.content.toJSON().content || [];
          if (contentNodes.length === 0) return false;

          const cellContents = distributeContent(contentNodes, columns, true, rows);

          return commands.insertContentAt(
            { from, to },
            { type: this.name, attrs: { type: 'grid', columns, rows }, content: cellContents }
          );
        },
      swapColumns:
        (fromIndex: number, toIndex: number) =>
        ({ commands, state }) => {
          const layoutInfo = findLayoutNode(state);
          if (!layoutInfo) return false;

          const { pos: layoutPos, node: layoutNode } = layoutInfo;
          const fromChild = layoutNode.child(fromIndex);
          const toChild = layoutNode.child(toIndex);
          
          if (!fromChild || !toChild) return false;

          const content = [];
          for (let i = 0; i < layoutNode.childCount; i++) {
            if (i === fromIndex) {
              content.push(toChild.toJSON());
            } else if (i === toIndex) {
              content.push(fromChild.toJSON());
            } else {
              content.push(layoutNode.child(i).toJSON());
            }
          }
          
          return commands.insertContentAt(
            { from: layoutPos, to: layoutPos + layoutNode.nodeSize },
            { type: this.name, attrs: layoutNode.attrs, content }
          );
        },
    };
  },
});
